import Box from "@mui/material/Box";
import { formatCount } from "@/features/deployment-dashboard/shared/helpers";
import type {
  DashboardAreaRow,
  DashboardPopulationMetrics,
  DashboardSourceState,
  DashboardThroughputMetrics,
  DashboardWidgetStatus,
} from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";
import { MetricCard } from "@/features/deployment-dashboard/widgets/metrics/dashboard-metrics/metric-card";
import {
  getDashboardWidgetIssue,
  type DashboardWidgetIssueDetails,
} from "@/features/deployment-dashboard/widgets/dashboard-widget-issue-indicator";
import { ThroughputIcon, TrendingUpIcon } from "@/shared/ui/console-icons";

interface DashboardMetricsPanelProps {
  population: DashboardSourceState<DashboardPopulationMetrics>;
  throughput: DashboardSourceState<DashboardThroughputMetrics>;
  timezone: string | null;
  areas: DashboardSourceState<DashboardAreaRow[]>;
}

function formatTimestamp(timestamp: string | null | undefined, timezone: string | null): string {
  if (timestamp == null) return "—";
  return new Date(timestamp).toLocaleString(undefined, {
    ...(timezone == null ? {} : { timeZone: timezone }),
  });
}

function getMetricContentState(status: DashboardWidgetStatus): "loading" | "empty" | undefined {
  if (status === "loading") return "loading";
  if (status === "empty") return "empty";
  return undefined;
}

export function DashboardMetricsPanel({
  population,
  throughput,
  timezone,
  areas,
}: DashboardMetricsPanelProps) {
  const departuresLounge = areas.data?.find(
    (area) => area.name.toLowerCase().includes("departures") || area.name === "Departures Lounge"
  );
  const departuresCapacityValue = departuresLounge?.capacity.value ?? null;
  const departuresCapacityStatus: DashboardWidgetStatus =
    areas.status === "loading"
      ? "loading"
      : departuresLounge == null
        ? areas.status === "ready"
          ? "empty"
          : areas.status
        : departuresLounge.capacity.status;

  const throughputValue = throughput.data?.throughputToday;
  const populationIssueDetails: DashboardWidgetIssueDetails = {
    unavailableAreaNames: areas.data
      ?.filter(
        (area) =>
          area.population.status === "unavailable" &&
          area.population.unavailableReason !== "not_configured"
      )
      .map((area) => area.name),
    notConfiguredAreaNames: areas.data
      ?.filter((area) => area.population.unavailableReason === "not_configured")
      .map((area) => area.name),
  };
  const allPopulationAreasNotConfigured =
    areas.data != null &&
    areas.data.length > 0 &&
    populationIssueDetails.notConfiguredAreaNames?.length === areas.data.length;
  const populationIssueStatus =
    population.status === "unavailable" && allPopulationAreasNotConfigured
      ? "partial"
      : population.status;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 2,
        alignItems: "start",
      }}
    >
      <MetricCard
        label="Departures Capacity"
        value={departuresCapacityValue == null ? "-" : formatCount(departuresCapacityValue)}
        issue={getDashboardWidgetIssue(departuresCapacityStatus)}
        contentState={getMetricContentState(departuresCapacityStatus)}
        icon={<ThroughputIcon sx={{ fontSize: "0.875rem" }} />}
      />
      <MetricCard
        label="Peak Population Today"
        value={
          population.data?.peakPopulationToday == null
            ? undefined
            : formatCount(population.data.peakPopulationToday)
        }
        valueSuffix="peak"
        issue={getDashboardWidgetIssue(populationIssueStatus, populationIssueDetails)}
        contentState={getMetricContentState(population.status)}
        icon={<TrendingUpIcon sx={{ fontSize: "0.875rem" }} />}
        note={`Recorded at ${formatTimestamp(population.data?.peakRecordedAt, timezone)}`}
      />
      <MetricCard
        label="Throughput Today"
        value={throughputValue == null ? undefined : formatCount(throughputValue)}
        issue={getDashboardWidgetIssue(throughput.status)}
        contentState={getMetricContentState(throughput.status)}
        icon={<ThroughputIcon sx={{ fontSize: "0.875rem" }} />}
        note={`Since ${formatTimestamp(throughput.data?.throughputSince, timezone)}`}
      />
    </Box>
  );
}
